import React from 'react';
import ReactDOM from 'react-dom/client';
import './App.css';
import App from './App.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import errorHandler from './utils/errorHandler';
import { initializeElectronStorage } from './utils/electronLocalStorageWrapper';
import { registerStorageBackupBridge } from './infrastructure/storage/storageBackupBridge';
import { registerStorageHealthBridge } from './infrastructure/storage/storageHealthBridge';
import { applyAutomaticContrast } from './utils/autoContrast';
import { initAppearancePreferences } from './services/appearancePreferences';
import { initBootTelemetry, markBootGate } from './infrastructure/telemetry/bootTelemetry';
import { installCrashDebugGlobal } from './infrastructure/telemetry/crashDebug';

applyAutomaticContrast();
initAppearancePreferences();
initBootTelemetry();
installCrashDebugGlobal();

// ADR-0014 Phase 4: cancel stale deferred boot warm on hash navigation
void import('./infrastructure/startup/bootIdleScheduler')
  .then((m) => m.ensureBootIdleNavigationCancel())
  .catch(() => {});

async function registerElectronQuitFlush() {
  if (typeof window === 'undefined' || !window.electronAPI?.onPrepareQuit) {
    return;
  }
  window.electronAPI.onPrepareQuit(async () => {
    try {
      const { runDailyClosingBeforeQuit } = await import(
        './domain/operations/dailyClosingOrchestrator'
      );
      await runDailyClosingBeforeQuit({ timeoutMs: 1800 });
    } catch (error) {
      console.warn('[APP QUIT] Daily closing check failed:', error?.message || error);
    }
    try {
      const { flushStorageForPowerLoss } = await import(
        './infrastructure/storage/powerLossGuard'
      );
      await flushStorageForPowerLoss({ forceSnapshot: true });
    } catch (error) {
      console.warn('[APP QUIT] Power-loss flush failed:', error?.message || error);
    }
    try {
      const { runDailyBackupIfDue } = await import(
        './infrastructure/storage/dailyBackupService'
      );
      await runDailyBackupIfDue();
    } catch (error) {
      console.warn('[APP QUIT] Daily backup failed:', error?.message || error);
    }
    try {
      await window.electronAPI.quitReady();
    } catch (error) {
      console.warn('[APP QUIT] quitReady failed:', error?.message || error);
    }
  });
}

// ✅ INIT: Electron-Storage-Diagnose (kein localStorage-Patch — ADR-0002)
try {
  const initResult = initializeElectronStorage();
  if (initResult.success) {
    console.log('✅ [APP INIT] Electron storage initialized:', {
      isElectron: initResult.isElectron,
      storageAvailable: initResult.storageAvailable,
      usage: initResult.usage
    });
  }
  markBootGate('electron_storage_probe_done');
} catch (error) {
  console.error('❌ [APP INIT] Failed to initialize Electron storage:', error);
}

registerElectronQuitFlush();
registerStorageBackupBridge();
registerStorageHealthBridge();

void errorHandler;

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary
      onError={(error, info) => {
        errorHandler.handle(error, 'React ErrorBoundary', {
          componentStack: info?.componentStack
        });
      }}
    >
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
